import { useEffect, useState } from "react";
import api from "../api/axios";
import AlertBox from "../components/AlertBox";
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaTags } from "react-icons/fa";
import { useTranslation } from "react-i18next";

export default function AdminCategories() {
    const { t } = useTranslation();
    const [cats, setCats] = useState([]);
    const [loading, setLoading] = useState(true);

    const [editing, setEditing] = useState(null);
    const [name, setName] = useState("");

    const [err, setErr] = useState("");
    const [msg, setMsg] = useState("");
    const [busy, setBusy] = useState(false);

    const fetchCats = async () => {
        setLoading(true);
        setErr("");
        try {
            const res = await api.get("/admin/categories");
            const payload = res.data.data ?? res.data;
            setCats(payload.data ?? payload);
        } catch (e) {
            setErr(e.response?.data?.message || t("admin_cat_load_failed"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCats();
    }, []);

    const startCreate = () => {
        setEditing(null);
        setName("");
        setMsg("");
        setErr("");
    };

    const startEdit = (c) => {
        setEditing(c);
        setName(c.name || "");
        setMsg("");
        setErr("");
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const cancel = () => {
        setEditing(null);
        setName("");
    };

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        setMsg("");
        setErr("");

        try {
            if (editing) {
                await api.put(`/admin/categories/${editing.id}`, { name });
                setMsg(t("admin_cat_update_success"));
            } else {
                await api.post("/admin/categories", { name });
                setMsg(t("admin_cat_create_success"));
            }
            cancel();
            fetchCats();
        } catch (e) {
            const data = e.response?.data;
            if (data?.errors) {
                const firstKey = Object.keys(data.errors)[0];
                setErr(data.errors[firstKey][0]);
            } else {
                setErr(data?.message || t("admin_cat_save_failed"));
            }
        } finally {
            setBusy(false);
        }
    };

    const remove = async (id) => {
        const ok = window.confirm(t("admin_cat_confirm_delete"));
        if (!ok) return;

        setBusy(true);
        setMsg("");
        setErr("");

        try {
            await api.delete(`/admin/categories/${id}`);
            setMsg(t("admin_cat_delete_success"));
            fetchCats();
        } catch (e) {
            setErr(e.response?.data?.message || t("admin_cat_delete_failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="container-fluid p-0 pb-5">
            <div className="d-flex align-items-center justify-content-between mb-4 border-bottom pb-3">
                <div className="d-flex align-items-center">
                    <FaTags className="fs-3 text-primary me-3" />
                    <h2 className="m-0 fw-bold text-dark">{t("admin_cat_title")}</h2>
                </div>
                <button className="btn btn-primary shadow-sm fw-bold" onClick={startCreate}>
                    <FaPlus className="me-2" /> {t("admin_cat_new")}
                </button>
            </div>

            <AlertBox type="success" message={msg} onClose={() => setMsg("")} />
            <AlertBox type="danger" message={err} onClose={() => setErr("")} />

            <div className="card shadow-sm mb-5 border-0 rounded-4 overflow-hidden">
                <div className={`card-header text-white pt-3 pb-2 ${editing ? 'bg-warning text-dark' : 'bg-primary'}`}>
                    <h5 className="m-0 fw-bold">
                        {editing ? (
                            <><FaEdit className="me-2" /> {t("admin_cat_edit")}{editing.id}</>
                        ) : (
                            <><FaPlus className="me-2" /> {t("admin_cat_add_new")}</>
                        )}
                    </h5>
                </div>

                <div className="card-body p-4 bg-light">
                    <form onSubmit={save} className="row g-3 align-items-end">
                        <div className="col-md-8">
                            <label className="form-label fw-bold">{t("admin_cat_name")} <span className="text-danger">*</span></label>
                            <input
                                className="form-control form-control-lg shadow-none"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                placeholder={t("admin_cat_name_ph")}
                            />
                        </div>

                        <div className="col-md-4 d-flex gap-2">
                            <button className="btn btn-success btn-lg w-100 fw-bold shadow-sm" disabled={busy}>
                                {busy ? <span className="spinner-border spinner-border-sm me-2"></span> : <FaSave className="me-2" />}
                                {busy ? t("admin_cat_saving") : t("admin_cat_save")}
                            </button>

                            {editing && (
                                <button type="button" className="btn btn-outline-secondary btn-lg w-100 fw-bold shadow-sm" onClick={cancel} disabled={busy}>
                                    <FaTimes className="me-2" /> {t("admin_cat_cancel")}
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </div>

            <div className="card shadow-sm border-0 rounded-4">
                <div className="card-header bg-white pt-4 pb-0 border-bottom-0">
                    <h5 className="fw-bold m-0">{t("admin_cat_registered")}</h5>
                </div>

                <div className="card-body p-0 mt-3">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }} />
                        </div>
                    ) : cats.length === 0 ? (
                        <div className="alert alert-info m-4 text-center py-5 rounded-3">
                            <FaTags className="display-4 mb-3 opacity-50" />
                            <h5 className="m-0">{t("admin_cat_no_found")}</h5>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle text-center mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="py-3" style={{ width: "100px" }}>{t("admin_cat_id")}</th>
                                        <th className="py-3 text-start ps-4">{t("admin_cat_name")}</th>
                                        <th className="py-3">{t("admin_cat_slug")}</th>
                                        <th className="py-3" style={{ width: "200px" }}>{t("admin_cat_actions")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cats.map((c) => (
                                        <tr key={c.id}>
                                            <td className="fw-bold text-muted">#{c.id}</td>
                                            <td className="text-start ps-4 fw-bold text-dark fs-5">{c.name}</td>
                                            <td>
                                                <span className="badge bg-light text-secondary border px-3 py-2">
                                                    {c.slug}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="d-flex justify-content-center gap-2">
                                                    <button className="btn btn-outline-primary btn-sm px-3 rounded-pill" onClick={() => startEdit(c)} disabled={busy}>
                                                        <FaEdit className="me-1" /> {t("admin_cat_btn_edit")}
                                                    </button>
                                                    <button className="btn btn-outline-danger btn-sm px-3 rounded-pill" onClick={() => remove(c.id)} disabled={busy}>
                                                        <FaTrash className="me-1" /> {t("admin_cat_btn_delete")}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}